import { Component } from '@theme/component';
import { DEFAULT_CASE_SIZE, recommend, markImports } from '@theme/club-scoring';
import { track, entryFrom, QUIZ_QUESTIONS } from '@theme/club-events';
import { profileKey, answersKey } from '@theme/club-case';
import { announce } from '@theme/live-status';
import { QUIZ_KEY, readCase, clearAll, isFresh } from '@theme/club-case-store';
import {
  advanceDelay,
  answerOf,
  canContinue,
  createAdvanceGuard,
  firstUnanswered,
  nextAfter,
  returnPath,
  picturesFor,
  questionFor,
  seedFrom,
  settle,
  skipped,
  skips,
  ticksFrom,
  walk,
} from '@theme/club-quiz-state';

/**
 * The wine club quiz: a welcome, then the question blocks one screen at a time, then it scores
 * the eligible wines (wine-club/SPEC-build-your-case-takeover.md on project-docs, section 3).
 *
 * The scoring is in club-scoring.js and follows wine-club/quiz-spec.md: colour and price EXCLUDE a
 * wine, everything else RANKS it, and a wine missing a field scores zero on that axis rather than
 * dropping out. This file only steps through the screens and hands the answers over.
 *
 * Progressive enhancement is deliberate: until this runs, the welcome and every question are
 * visible and answerable as a plain form. The component hides all but the current screen on
 * connect, never the server.
 *
 * THE RULES LIVE IN club-quiz-state.js, where a Node test can hold them: a single-choice answer
 * moves on once, the blank answer on a tick-box question is exclusive, Continue waits for an
 * answer on a single-choice question and reads Skip on a tick-box question with nothing ticked,
 * and an answer ticked before this file booted counts. This file is browser-only and nothing in
 * CI runs it.
 *
 * BOOTING INTO A FORM SOMEBODY IS ALREADY USING. On a slow connection the start link and the
 * answers work before this file arrives. So it reads the ticks the form already holds, keeps
 * them, and when there are any (or the start link's fragment is in the address) opens on the
 * first unanswered question instead of putting the welcome back over the visitor.
 *
 * A SINGLE-CHOICE ANSWER MOVES ON WHEN IT IS TAPPED, after a short beat. Arrow keys also change a
 * radio, and moving on at each arrow press would take the question away from someone still
 * reading the answers, so an answer chosen with the arrow keys is saved and waits for Continue.
 *
 * ONE H1 AT A TIME. The welcome's h1 is hidden with the welcome. The question on screen has its
 * title given the heading role at level 1, and loses it again when it leaves.
 *
 * It sends six events: club_entry_viewed and club_welcome_viewed on connect, club_quiz_started on
 * the first answer, club_quiz_answered once per question as the visitor moves on from it (on
 * Continue, or on the tap or Space that moves on by itself) with the answer they left it on (a
 * tick-box question's values joined by commas, blank when it was skipped), club_resumed when a returning visitor
 * picks up where they left off, and club_quiz_completed after the last question. On completion it
 * hands the pool over to wine-club:complete, so the result screen can build the case without
 * refetching it. Picking up a quiz that was already finished goes straight to the profile:
 * club_resumed carries the step after the last question, and club_quiz_completed is not sent a
 * second time, because nothing was completed twice.
 *
 * THE BAR STAYS WHEN THE QUESTIONS GO. After the last question the quiz hides its screens and its
 * footer and keeps the bar over the result section, which is the only part of the frame the
 * reveal and the case need. The bar is drawn once, here, and the result section never reaches
 * into it. Three document events carry everything between the two:
 *   wine-club:view     from the result, `detail.view` "reveal" or "case": the screen it now shows.
 *                      The bar takes that screen's label and marks every segment done.
 *   wine-club:back     from here, when Back is pressed over either of them. The result steps back
 *                      itself, case to reveal or reveal to nothing, and from the reveal this file
 *                      puts the last question back on screen.
 *   wine-club:restart  from the result's "Change my answers": back to question 1, as before.
 *   wine-club:edit     from a reveal pill (colour or price), `detail.axis`: that question opens with
 *                      every other answer kept, and moving on from it goes straight back to the
 *                      result (club-quiz-state.js `nextAfter`), built afresh from the answers.
 *   wine-club:start-again  from the result's Start again: the answers and the saved case are
 *                      forgotten, as the welcome's Start again forgets them, and question 1 opens
 *                      with nothing ticked. The result's entry is taken over by question 1.
 * Two more go the other way, from here to the result: wine-club:hide when the browser's Back or
 * Forward lands on a question, and `detail.view` on wine-club:complete, which says whether the
 * result opens on the reveal or on the case.
 *
 * EVERY SCREEN IS A HISTORY ENTRY, so the browser's Back and a phone's swipe go back a screen and
 * Forward goes on one. Each entry's state carries `club: { screen, prev }`: the screen's name from
 * club-quiz-state.js `screens` ("welcome", "q0" to "q5", "reveal", "case") and the name of the
 * entry under it. The address never changes, so the page stays shareable, and a reload lands on
 * the screen its entry names, because the browser keeps an entry's state across a reload.
 *   - showing a screen pushes an entry, unless the entry on now already names it: that is how a
 *     screen re-shown, or shown because Back or Forward landed on it, adds nothing;
 *   - the funnel's own Back arrow is the browser's Back whenever the entry under this one is the
 *     funnel's, so the two cannot disagree. With nothing of the funnel's under it (a visitor who
 *     arrived on a question) it steps back a screen and takes this entry over;
 *   - picking up (the welcome's main button, or arriving with ?resume=1) makes an entry for each
 *     screen the visitor would have walked through to get there, club-quiz-state.js `walk`, so
 *     Back from a picked-up case is the reveal, then the questions. Never the welcome they did
 *     not see: from ?resume=1 the first question takes over the arrival's own entry.
 *
 * THE ANSWERS AND THE CASE ARE KEPT FOR FOURTEEN DAYS (assets/club-case-store.js, which owns the
 * keys and the number). The answers are stamped `savedAt` at every write; older than that, or
 * unstamped, they are forgotten when read. `?resume=1` opens on the saved case when there is one
 * built from the saved answers, on the first unanswered question when the quiz was left part way,
 * and on the welcome when nothing is saved. Without it the welcome offers the same thing.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} questions - One group per question block.
 * @property {HTMLElement} welcome - The welcome screen.
 * @property {HTMLElement} welcomeHeading - Its h1.
 * @property {HTMLElement} segments - The progress segments, one child per question.
 * @property {HTMLElement} progress - The progress label, in words.
 * @property {HTMLButtonElement} barBack - Back, in the top bar.
 * @property {HTMLElement} resume - The returning visitor's notice.
 * @property {HTMLElement} resumeCount - How far they got.
 * @property {HTMLButtonElement} startAgain - Start again, in the notice.
 * @property {HTMLAnchorElement} start - The start link, which becomes Pick up where I left off.
 * @property {HTMLElement} nav - The footer.
 * @property {HTMLButtonElement} back - Previous screen.
 * @property {HTMLButtonElement} next - Continue, or Skip.
 *
 * @extends {Component<Refs>}
 */

const WELCOME = -1;

class WineClubQuiz extends Component {
  requiredRefs = [
    'questions', 'welcome', 'welcomeHeading', 'segments', 'progress', 'barBack',
    'resume', 'resumeCount', 'startAgain', 'start', 'nav', 'back', 'next',
  ];

  /** @type {number} WELCOME, or the index of the question on screen */
  #step = WELCOME;
  /** @type {Map<string, string>} */
  #answers = new Map();
  /** @type {Map<string, string>} the last answer sent per axis, so one is never sent twice */
  #sent = new Map();
  /** @type {object[]} */
  #pool = [];
  /** @type {string} */
  #entry = 'direct';
  /** @type {number} 0 until the first answer */
  #startedAt = 0;
  /** club_quiz_started goes once per visit, however often the visitor starts again. */
  #started = false;
  #guard = createAdvanceGuard();
  /** @type {number} */
  #timer = 0;
  /** True while an arrow key is held on a radio: the change that follows is not a tap. */
  #arrowed = false;
  /** True while the welcome offers to pick up a quiz that has answers, finished or not. */
  #resuming = false;
  /** @type {'' | 'reveal' | 'case'} the result section's screen under the bar; '' while the quiz has the page */
  #view = '';
  /** True while Back steps back with no entry of the funnel's under this one: the entry is taken over, not added. */
  #replacing = false;
  /** True while one answer is being changed from a reveal pill: moving on returns to the result. */
  #editing = false;

  connectedCallback() {
    super.connectedCallback();

    this.#pool = this.#readPool();
    this.#flagRepeatedAxes();
    const underway = this.#restore();

    this.#pointClose();
    this.#entry = entryFrom(window.location.search);
    track('club_entry_viewed', { entry: this.#entry });
    document.addEventListener('wine-club:restart', this.#onRestart);
    document.addEventListener('wine-club:start-again', this.#onResultStartAgain);
    document.addEventListener('wine-club:edit', this.#onEdit);
    document.addEventListener('wine-club:view', this.#onView);
    window.addEventListener('popstate', this.#onPop);

    this.addEventListener('change', this.#onChange);
    this.addEventListener('click', this.#onClick);
    this.addEventListener('keydown', this.#onKeydown);
    this.addEventListener('keyup', this.#onPointerOrKeyup);
    this.addEventListener('pointerdown', this.#onPointerOrKeyup);
    this.addEventListener('shopify:block:select', this.#onBlockSelect);
    this.refs.next.addEventListener('click', this.#onNext);
    this.refs.back.addEventListener('click', this.#onBack);
    this.refs.barBack.addEventListener('click', this.#onBack);
    this.refs.start.addEventListener('click', this.#onStart);
    this.refs.startAgain.addEventListener('click', this.#onStartAgain);

    // Both are read, not one or the other: the second also takes the fragment out of the address.
    const jumped = this.#startedBeforeBoot();
    const resume = this.#resumeAsked();
    const straightIn = this.#startAsked();
    const landed = window.history.state?.club?.screen;
    if (typeof landed === 'string') {
      // A reload, or Back or Forward into this page from another: the entry says where they were.
      this.#goTo(landed, { focus: false });
    } else if (resume && this.#answers.size) {
      this.#pickUp({ arriving: true });
    } else if (underway || jumped || straightIn) {
      const first = firstUnanswered(this.#axes(), this.#answers);
      if (this.#answers.size) this.#begin();
      this.#show(first === -1 ? this.refs.questions.length - 1 : first, { focus: false, record: 'replace' });
    } else {
      this.#show(WELCOME, { focus: false, record: 'replace' });
    }
    // Only when the welcome is what they see. It used to be sent on every load, so it always
    // equalled club_entry_viewed, including for anyone landing on a question or their case
    // (GV-38 audit, 2026-10-04). #screen() reads the result's view, set synchronously above.
    if (this.#screen() === 'welcome') track('club_welcome_viewed', { entry: this.#entry });
  }

  disconnectedCallback() {
    window.clearTimeout(this.#timer);
    this.removeEventListener('change', this.#onChange);
    this.removeEventListener('click', this.#onClick);
    this.removeEventListener('keydown', this.#onKeydown);
    this.removeEventListener('keyup', this.#onPointerOrKeyup);
    this.removeEventListener('pointerdown', this.#onPointerOrKeyup);
    this.removeEventListener('shopify:block:select', this.#onBlockSelect);
    this.refs.next.removeEventListener('click', this.#onNext);
    this.refs.back.removeEventListener('click', this.#onBack);
    this.refs.barBack.removeEventListener('click', this.#onBack);
    this.refs.start.removeEventListener('click', this.#onStart);
    this.refs.startAgain.removeEventListener('click', this.#onStartAgain);
    document.removeEventListener('wine-club:restart', this.#onRestart);
    document.removeEventListener('wine-club:start-again', this.#onResultStartAgain);
    document.removeEventListener('wine-club:edit', this.#onEdit);
    document.removeEventListener('wine-club:view', this.#onView);
    window.removeEventListener('popstate', this.#onPop);
    super.disconnectedCallback();
  }

  /**
   * Editor only. Two questions on one axis leave only the later answer, and a theme block cannot
   * read another block's settings, so Liquid cannot see it. blocks/quiz-question.liquid renders a
   * hidden warning on every question while `request.design_mode` is true, and never otherwise;
   * this shows it where the axis repeats. On the storefront there is nothing to find.
   */
  #flagRepeatedAxes() {
    const axes = this.refs.questions.map((field) => field.dataset.axis ?? '');
    for (const field of this.refs.questions) {
      const warning = field.querySelector('[data-quiz-axis-warning]');
      if (warning instanceof HTMLElement) warning.hidden = axes.filter((axis) => axis === field.dataset.axis).length < 2;
    }
  }

  /**
   * The pool is a JSON payload Liquid rendered from the eligible collection. A malformed or absent
   * payload leaves the quiz answerable and the result empty, rather than throwing on connect.
   */
  #readPool() {
    const node = this.querySelector('[data-club-pool]');
    if (!(node instanceof HTMLElement) || !node.textContent) return [];
    try {
      const parsed = JSON.parse(node.textContent);
      // Givino's own imports, from the theme setting the snippet hands over (club-scoring.js).
      return Array.isArray(parsed) ? markImports(parsed, node.dataset.imports) : [];
    } catch {
      return [];
    }
  }

  /** @param {Element | null | undefined} field */
  #inputs(field) {
    return field ? /** @type {HTMLInputElement[]} */ ([...field.querySelectorAll('input')]) : [];
  }

  /** @param {HTMLInputElement[]} inputs */
  #boxes(inputs) {
    return inputs.map(({ value, checked }) => ({ value, checked }));
  }

  /**
   * The words on the card(s) chosen for a question, for reports that people read. Tells "Surprise
   * me" and "Nothing in particular" (stored as '') apart from a question skipped with nothing
   * ticked, which reads "Skipped".
   * @param {string} axis
   */
  #labelOf(axis) {
    const field = this.refs.questions.find((question) => question.dataset.axis === axis);
    if (!field) return '';
    const words = [...field.querySelectorAll('input:checked')]
      .map((input) => input.closest('label')?.querySelector('.quiz-card__label')?.textContent?.trim() ?? '')
      .filter(Boolean);
    return words.length ? words.join(', ') : 'Skipped';
  }

  /** @param {Element | null | undefined} field */
  #axisOf(field) {
    return field instanceof HTMLElement ? field.dataset.axis : undefined;
  }

  /** @param {Element | null | undefined} field */
  #isMultiple(field) {
    return field instanceof HTMLElement && field.hasAttribute('data-multiple');
  }

  /**
   * Put the visitor's answers back: what the form already holds, then what was saved. The form
   * wins where it has an answer, because a tick made before this file booted is newer than
   * anything in storage.
   * @returns {boolean} true when the form held an answer that storage did not, which is a visitor
   *   who was answering before the boot
   */
  #restore() {
    /** @type {Record<string, unknown>} */
    let stored = {};
    try {
      const saved = JSON.parse(localStorage.getItem(QUIZ_KEY) ?? 'null');
      // Older than fourteen days, or never stamped, is as good as never saved (club-case-store.js).
      if (saved && !isFresh(saved.savedAt)) localStorage.removeItem(QUIZ_KEY);
      else if (saved?.answers && typeof saved.answers === 'object') stored = saved.answers;
    } catch {
      stored = {};
    }

    let underway = false;
    for (const field of this.refs.questions) {
      const axis = this.#axisOf(field);
      if (!axis) continue;
      const inputs = this.#inputs(field);
      const multiple = this.#isMultiple(field);

      const seed = seedFrom(this.#boxes(inputs), multiple);
      if (seed.answer !== null) {
        inputs.forEach((input, index) => {
          input.checked = Boolean(seed.ticks[index]);
        });
        this.#answers.set(axis, seed.answer);
        if (!(axis in stored) || String(stored[axis]) !== seed.answer) underway = true;
        continue;
      }

      if (!(axis in stored)) continue;
      const value = String(stored[axis]);

      if (multiple) {
        const ticks = ticksFrom(inputs.map((input) => input.value), value);
        if (!ticks.some(Boolean)) continue;
        inputs.forEach((input, index) => {
          input.checked = Boolean(ticks[index]);
        });
        const answer = answerOf(this.#boxes(inputs));
        if (answer !== null) this.#answers.set(axis, answer);
        continue;
      }

      const input = inputs.find((candidate) => candidate.value === value);
      if (input) {
        input.checked = true;
        this.#answers.set(axis, value);
      }
    }

    if (underway) this.#save();
    return underway;
  }

  /**
   * With no script the start link jumps to the questions, and it leaves its fragment in the
   * address. Finding it there at boot means the visitor pressed Start before this file arrived.
   * The fragment is taken back out, so a reload shows the welcome like any other.
   */
  #startedBeforeBoot() {
    const { hash, pathname, search } = window.location;
    if (!hash || hash !== this.refs.start.hash) return false;
    window.history.replaceState(window.history.state, '', pathname + search);
    return true;
  }

  /**
   * `?resume=1`, which the site's own links carry when there is something to pick up. It is taken
   * out of the address once read, so the address stays the one to share and a reload of it does
   * not pick up a second time.
   */
  #resumeAsked() {
    const url = new URL(window.location.href);
    if (url.searchParams.get('resume') !== '1') return false;
    url.searchParams.delete('resume');
    window.history.replaceState(window.history.state, '', url.pathname + url.search + url.hash);
    return true;
  }

  /**
   * `?start=1`, which the header's Build your case links carry: they open on the first question
   * rather than the welcome (Matt, 2026-10-05). Saved answers still win: with `resume=1` as well
   * the visitor picks up, and without it they land on their first unanswered question, as a
   * visitor pressing Start does. Taken out of the address once read, like `resume`.
   */
  #startAsked() {
    const url = new URL(window.location.href);
    if (url.searchParams.get('start') !== '1') return false;
    url.searchParams.delete('start');
    window.history.replaceState(window.history.state, '', url.pathname + url.search + url.hash);
    return true;
  }

  /**
   * Close goes back to the page the quiz was opened from (club-quiz-state.js `returnPath`). The
   * arrival's referrer is kept for the visit, because a reload or a step through the quiz's own
   * history makes the builder its own referrer. Without one Close keeps its link, the Wine Club page.
   */
  #pointClose() {
    const close = this.querySelector('.wine-club-quiz__icon--close');
    if (!(close instanceof HTMLAnchorElement)) return;
    const key = 'givino:wine-club-quiz-return';
    let back = returnPath(document.referrer, window.location.origin, window.location.pathname);
    try {
      if (back) sessionStorage.setItem(key, back);
      else back = sessionStorage.getItem(key);
    } catch {
      // Blocked storage: the arrival's referrer still works for this page view.
    }
    if (back) close.href = back;
  }

  /** Each question's axis, in order. */
  #axes() {
    return this.refs.questions.map((field) => field.dataset.axis ?? '');
  }

  #save() {
    try {
      localStorage.setItem(
        QUIZ_KEY,
        JSON.stringify({ answers: Object.fromEntries(this.#answers), savedAt: Date.now() })
      );
    } catch {
      // A private window or blocked storage is not a reason to break the quiz.
    }
  }

  #forget() {
    this.#answers.clear();
    this.#sent.clear();
    this.#startedAt = 0;
    for (const field of this.refs.questions) {
      for (const input of this.#inputs(field)) input.checked = false;
    }
    // The saved case goes with the answers: it was built from them, and a visitor starting again
    // is not to be offered it back, here or anywhere else on the site.
    clearAll();
  }

  #begin() {
    if (!this.#startedAt) this.#startedAt = Date.now();
    if (this.#started) return;
    this.#started = true;
    track('club_quiz_started', { entry: this.#entry });
  }

  /**
   * @param {HTMLElement} field
   * @param {string} answer
   */
  #send(field, answer) {
    const axis = this.#axisOf(field);
    if (!axis || this.#sent.get(axis) === answer) return;
    this.#sent.set(axis, answer);
    const step = this.refs.questions.indexOf(field) + 1;
    track('club_quiz_answered', { step, question: axis, answer, answer_label: this.#labelOf(axis) });
  }

  /** @param {Event} event */
  #onChange = (event) => {
    const input = event.target;
    if (!(input instanceof HTMLInputElement)) return;
    const field = input.closest('[data-axis]');
    const axis = this.#axisOf(field);
    if (!(field instanceof HTMLElement) || !axis) return;

    if (input.type === 'checkbox') {
      const inputs = this.#inputs(field);
      settle(this.#boxes(inputs), inputs.indexOf(input)).forEach((checked, index) => {
        const box = inputs[index];
        if (box) box.checked = checked;
      });
      const answer = answerOf(this.#boxes(inputs));
      if (answer === null) this.#answers.delete(axis);
      else this.#answers.set(axis, answer);
      this.#begin();
      this.#save();
      this.#syncNext();
      this.#lookAhead();
      return;
    }

    if (input.type !== 'radio') return;
    // Not sent here. Arrow keys change a radio at every press, so sending on change reported each
    // answer the visitor passed on the way to the one they meant (loop 2 club check, 2026-10-01).
    // The answer is sent when they move on: #advance for a tap or Space, #onNext for Continue.
    this.#answers.set(axis, input.value);
    this.#begin();
    this.#save();
    this.refs.next.disabled = false;
    this.#lookAhead();
    if (!this.#arrowed) this.#advance();
  };

  /**
   * A tap on the answer that is already chosen changes nothing, so no `change` follows it. Coming
   * Back and tapping the same card again should still move on, as it does in the design.
   * @param {Event} event
   */
  #onClick = (event) => {
    const input = event.target;
    if (!(input instanceof HTMLInputElement) || input.type !== 'radio' || this.#arrowed) return;
    const axis = this.#axisOf(input.closest('[data-axis]'));
    if (axis && this.#answers.get(axis) === input.value) this.#advance();
  };

  /** @param {KeyboardEvent} event */
  #onKeydown = (event) => {
    if (event.key.startsWith('Arrow') && event.target instanceof HTMLInputElement) this.#arrowed = true;
  };

  #onPointerOrKeyup = () => {
    this.#arrowed = false;
  };

  /**
   * The theme editor selects a block that may be on a screen nobody is looking at. Show it.
   * @param {Event} event
   */
  #onBlockSelect = (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;
    const index = this.refs.questions.findIndex((field) => field === target || field.contains(target));
    if (index >= 0) this.#show(index, { focus: false, record: false });
    else if (this.refs.welcome.contains(target)) this.#show(WELCOME, { focus: false, record: false });
  };

  /** Move on from the question on screen, once, after the beat, and send the answer it was left on. */
  #advance() {
    const target = this.#guard.tap(this.#step);
    if (target === null) return;
    const field = this.refs.questions[this.#step];
    const answer = this.#answers.get(this.#axisOf(field) ?? '');
    if (field && answer != null) this.#send(field, answer);
    const to = this.#nextFrom(this.#step);
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.#timer = window.setTimeout(() => this.#go(to), advanceDelay(reduced));
  }

  /**
   * Where moving on from question `index` goes: the next one, or, while an answer is being changed
   * from the reveal, back to the result (club-quiz-state.js `nextAfter`).
   * @param {number} index
   */
  #nextFrom(index) {
    return nextAfter(index, this.refs.questions.length, {
      editing: this.#editing,
      firstUnanswered: firstUnanswered(this.#axes(), this.#answers),
    });
  }

  /** @param {number} index */
  #go(index) {
    if (index >= this.refs.questions.length) this.#complete();
    else this.#show(index);
  }

  #onNext = () => {
    if (this.#guard.busy() || this.#step === WELCOME) return;
    const field = this.refs.questions[this.#step];
    const axis = this.#axisOf(field);
    if (field && axis && this.#isMultiple(field)) {
      const inputs = this.#inputs(field);
      if (skips(this.#boxes(inputs), true)) {
        // Skip is the blank answer. The plain row is ticked, where there is one, so coming Back
        // shows what skipping chose.
        skipped(this.#boxes(inputs)).forEach((checked, index) => {
          const box = inputs[index];
          if (box) box.checked = checked;
        });
        this.#answers.set(axis, '');
        this.#begin();
        this.#save();
      }
    }
    // Every question's answer is sent here when Continue moves on from it, a single-choice one
    // included: one picked with the arrow keys waits for Continue, and is sent once, as it stands.
    const answer = axis ? this.#answers.get(axis) : undefined;
    if (field && answer != null) this.#send(field, answer);
    this.#go(this.#nextFrom(this.#step));
  };

  /**
   * Continue, and whether it can be pressed. On a tick-box question with nothing ticked it reads
   * Skip. The change of label is announced, once, when it changes: a button's new name is not
   * read out by itself, and saying it at every tick would be noise.
   * @param {{ quiet?: boolean }} [options] quiet when a new question is being shown, which has
   *   its own announcement
   */
  #syncNext({ quiet = false } = {}) {
    const { next, questions } = this.refs;
    const field = questions[this.#step];
    const axis = this.#axisOf(field);
    const multiple = this.#isMultiple(field);
    next.disabled = !axis || !canContinue(this.#answers.get(axis), multiple);

    const skip = skips(this.#boxes(this.#inputs(field)), multiple);
    const label = skip ? next.dataset.skipLabel : next.dataset.continueLabel;
    if (!label || next.textContent?.trim() === label) return;
    next.textContent = label;
    if (!quiet) announce(label);
  }

  #onBack = () => {
    // The entry under this one is the funnel's: go there as the browser's Back would, and the
    // popstate that follows shows it. The two Backs are then one Back.
    if (window.history.state?.club?.prev) {
      window.history.back();
      return;
    }
    // Nothing of the funnel's under this entry. Step back a screen and take the entry over, so
    // the browser's Back from here still leaves the page, as it would have.
    this.#replacing = true;
    try {
      if (this.#view) {
        // Over the result section. It steps back itself; from the reveal there is nothing of its
        // own left to show, so the last question comes back.
        const from = this.#view;
        document.dispatchEvent(new CustomEvent('wine-club:back'));
        if (from === 'reveal') this.#show(this.refs.questions.length - 1, { record: 'replace' });
        return;
      }
      if (this.#step > WELCOME) this.#show(this.#step - 1, { record: 'replace' });
    } finally {
      this.#replacing = false;
    }
  };

  /**
   * The browser's Back or Forward landed on one of the funnel's entries.
   * @param {PopStateEvent} event
   */
  #onPop = (event) => {
    const screen = event.state?.club?.screen;
    if (typeof screen === 'string') this.#goTo(screen);
  };

  /**
   * Show the screen an entry names, adding no entry: the one on now already names it.
   * @param {string} screen
   * @param {{ focus?: boolean }} [options] focus false on arrival, as the first screen always was
   */
  #goTo(screen, { focus = true } = {}) {
    const { questions } = this.refs;
    // Back or Forward ends a change made from a pill: the screen the entry names is what shows.
    this.#editing = false;
    if (screen === 'reveal' || screen === 'case') {
      const first = firstUnanswered(this.#axes(), this.#answers);
      if (first === -1) {
        this.#complete({ resumed: true, view: screen });
        return;
      }
      // The answers the entry was made with are gone (forgotten, or older than fourteen days).
      // The first question they still need, and this entry says so from now on.
      this.#leaveResult();
      this.#show(first, { focus, record: 'replace' });
      return;
    }
    this.#leaveResult();
    const match = /^q(\d+)$/.exec(screen);
    const index = match ? Math.min(Number(match[1]), questions.length - 1) : WELCOME;
    this.#show(index, { focus });
  }

  /** Put the result section away, when Back or Forward goes from it to a screen of the quiz's. */
  #leaveResult() {
    if (!this.#view) return;
    document.dispatchEvent(new CustomEvent('wine-club:hide'));
  }

  /** The screen on now, by the name its history entry carries. */
  #screen() {
    if (this.#view) return this.#view;
    return this.#step === WELCOME ? 'welcome' : `q${this.#step}`;
  }

  /**
   * Give the screen on now a history entry: push one, unless the entry on now already names this
   * screen; or, with `replace`, take the entry on now over.
   * @param {{ replace?: boolean }} [options]
   */
  #record({ replace = false } = {}) {
    try {
      const state = window.history.state ?? {};
      const here = state.club;
      const screen = this.#screen();
      if (replace) {
        window.history.replaceState({ ...state, club: { screen, prev: here?.prev ?? null } }, '');
      } else if (here?.screen !== screen) {
        window.history.pushState({ ...state, club: { screen, prev: here?.screen ?? null } }, '');
      }
    } catch {
      // A browser that refuses history (a sandboxed frame) still steps through the screens.
    }
  }

  /**
   * One entry per screen a visitor picking up would have walked through to reach `to`.
   * @param {string} to
   * @param {boolean} arriving true on arrival with ?resume=1: the first screen takes over the
   *   arrival's own entry, so there is no entry for the welcome that was skipped
   */
  #walkTo(to, arriving) {
    const path = walk(arriving ? null : this.#screen(), to, this.refs.questions.length);
    try {
      path.forEach((screen, i) => {
        const state = window.history.state ?? {};
        const prev = arriving && i === 0 ? null : state.club?.screen ?? null;
        if (arriving && i === 0) window.history.replaceState({ ...state, club: { screen, prev } }, '');
        else window.history.pushState({ ...state, club: { screen, prev } }, '');
      });
    } catch {
      // As above.
    }
  }

  /**
   * The saved case, when there is one built from the answers saved now: what picking up opens on.
   * @returns {ReturnType<typeof readCase>}
   */
  #savedCase() {
    const saved = readCase();
    return saved && saved.answersKey === answersKey(Object.fromEntries(this.#answers)) ? saved : null;
  }

  /**
   * Pick up where the visitor left off: the first unanswered question, or with every question
   * answered the case they saved, or the profile when no case was saved from these answers.
   * @param {{ arriving?: boolean }} [options] arriving with ?resume=1, rather than from the welcome
   */
  #pickUp({ arriving = false } = {}) {
    const { questions } = this.refs;
    const first = firstUnanswered(this.#axes(), this.#answers);
    // Every question answered: the step after the last one, which is the profile.
    track('club_resumed', { step: (first === -1 ? questions.length : first) + 1 });
    const view = this.#savedCase() ? 'case' : 'reveal';
    this.#walkTo(first === -1 ? view : `q${first}`, arriving);
    if (first === -1) this.#complete({ resumed: true, view });
    else this.#show(first, { focus: !arriving });
  }

  /**
   * The pinned button. It starts the quiz, and for a returning visitor it picks the quiz up: the
   * button a thumb finds first must never be the one that throws the answers away.
   * @param {Event} event
   */
  #onStart = (event) => {
    // With no script this link jumps to the questions, which are all on the page. Here it steps.
    event.preventDefault();
    if (!this.#resuming) {
      this.#show(0);
      return;
    }
    this.#pickUp();
  };

  /** "Start again", the quiet button in the notice: the only thing that forgets the answers. */
  #onStartAgain = () => {
    this.#forget();
    this.#show(0);
  };

  /**
   * The welcome offers to pick up a quiz that has answers, whether or not it was finished. With a
   * case saved from those answers it says so, since the case is what the button opens on.
   */
  #offerResume() {
    const { questions, resume, resumeCount, start } = this.refs;
    const answered = questions.filter((field) => this.#answers.has(field.dataset.axis ?? '')).length;
    this.#resuming = answered > 0;
    resume.hidden = !this.#resuming;
    if (this.#resuming) {
      const saved = answered === questions.length ? this.#savedCase() : null;
      resumeCount.textContent = saved
        ? (resumeCount.dataset.caseTemplate ?? '').replace('{{ count }}', String(saved.lines.length))
        : (resumeCount.dataset.template ?? '')
            .replace('{{ answered }}', String(answered))
            .replace('{{ total }}', String(questions.length));
    }
    const label = this.#resuming ? start.dataset.resumeLabel : start.dataset.startLabel;
    if (label) start.textContent = label;
  }

  /**
   * @param {number} index WELCOME, or a question's index
   * @param {{ focus?: boolean, record?: boolean | 'replace' }} [options] `record` false shows the
   *   screen with no history entry (the theme editor's block select); 'replace' takes the entry
   *   on now over, rather than adding one
   */
  #show(index, { focus = true, record = true } = {}) {
    const { questions, welcome, welcomeHeading, progress, barBack, nav } = this.refs;
    window.clearTimeout(this.#timer);
    this.#guard.clear();
    this.#arrowed = false;
    this.#step = index;
    this.#view = '';
    delete this.dataset.view;

    const onWelcome = index === WELCOME;
    welcome.hidden = !onWelcome;
    nav.hidden = onWelcome;
    barBack.hidden = onWelcome;
    if (onWelcome) this.#offerResume();

    const heading = this.#onlyQuestion(index) ?? (onWelcome ? welcomeHeading : null);
    this.#syncNext({ quiet: true });
    this.#lookAhead();

    const label = onWelcome
      ? progress.dataset.welcomeLabel ?? ''
      : (progress.dataset.stepLabel ?? '')
          .replace('{{ step }}', String(index + 1))
          .replace('{{ total }}', String(questions.length));
    this.#progress(index, label);
    if (record) this.#record({ replace: record === 'replace' });

    if (!focus) return;
    // Focus the heading rather than the first answer, so a screen reader hears the question
    // first, and so arrowing does not immediately change a selection.
    heading?.focus();
    if (!onWelcome) announce(label);
  }

  /**
   * Get the pictures ready: the ones on the question on screen, in case it was reached by picking
   * up, and the next two questions', so they are fetched and decoded while this one is being
   * answered. Two, because a single-choice question can be answered in well under a second: one
   * ahead left every picture on the food question still loading when the question before it was
   * tapped at once. Run when a screen is shown and again at every answer, because an answer can
   * change which pictures the next question shows, and the time to fetch them is while the
   * visitor is still on this one.
   */
  #lookAhead() {
    this.#dress();
    this.#warm(this.#step);
    this.#warm(this.#step + 1);
    this.#warm(this.#step + 2);
  }

  /**
   * Mark every question with the set of pictures this visitor gets: the second set when their
   * colours hold no red. The block's stylesheet does the swap; both pictures are in the markup.
   */
  #dress() {
    const pictures = picturesFor(this.#answers.get('colour'));
    for (const field of this.refs.questions) {
      if (pictures) field.dataset.pictures = pictures;
      else delete field.dataset.pictures;
    }
  }

  /**
   * Fetch and decode a question's pictures ahead of its turn. They are lazy in the markup, for
   * the page with no script where all six questions are below the fold; left lazy here, every
   * picture box on the food question was still empty 120ms after it appeared on Fast 4G.
   * Where an answer carries two pictures only the one that will show is fetched.
   * @param {number} index a question's index; anything else does nothing
   */
  #warm(index) {
    const field = this.refs.questions[index];
    if (!field) return;
    const second = field.dataset.pictures === 'alt';
    for (const art of field.querySelectorAll('.quiz-card__art--photo')) {
      const img = (second && art.querySelector('.quiz-card__image--alt')) || art.querySelector('.quiz-card__image');
      if (!(img instanceof HTMLImageElement) || img.loading !== 'lazy') continue;
      img.loading = 'eager';
      // A picture that fails to decode is a card with its caption and no picture, not an error.
      img.decode().catch(() => {});
    }
  }

  /**
   * Leave one question on screen, or none, and move the level 1 heading role with it.
   * @param {number} index a question's index; anything else hides them all
   * @returns {HTMLElement | null} the title of the question now on screen
   */
  #onlyQuestion(index) {
    /** @type {HTMLElement | null} */
    let heading = null;
    this.refs.questions.forEach((field, i) => {
      const current = i === index;
      field.hidden = !current;
      const title = field.querySelector('[data-quiz-heading]');
      if (!(title instanceof HTMLElement)) return;
      if (current) {
        title.setAttribute('role', 'heading');
        title.setAttribute('aria-level', '1');
        heading = title;
      } else {
        title.removeAttribute('role');
        title.removeAttribute('aria-level');
      }
    });
    return heading;
  }

  /**
   * The bar's segments and its label, which is also the segments' accessible name.
   * @param {number} upTo the last segment to mark done
   * @param {string} label
   */
  #progress(upTo, label) {
    const { questions, segments, progress } = this.refs;
    [...segments.children].forEach((segment, i) => {
      segment.classList.toggle('is-done', i <= upTo);
      if (segment instanceof HTMLElement) segment.hidden = i >= questions.length;
    });
    progress.textContent = label;
    segments.setAttribute('aria-label', label);
  }

  /**
   * Give the page to the result section and keep the bar over it. Focus is the result section's:
   * it moves to the heading of the screen it shows.
   * @param {'reveal' | 'case'} view
   */
  #handOver(view) {
    const { questions, welcome, progress, barBack, nav } = this.refs;
    this.#view = view;
    this.dataset.view = view;
    welcome.hidden = true;
    nav.hidden = true;
    barBack.hidden = false;
    this.#onlyQuestion(-1);
    const label = view === 'case' ? progress.dataset.caseLabel : progress.dataset.profileLabel;
    this.#progress(questions.length - 1, label ?? '');
  }

  /** @param {Event} event */
  #onView = (event) => {
    const view = /** @type {CustomEvent} */ (event).detail?.view;
    if (view !== 'reveal' && view !== 'case') return;
    this.#handOver(view);
    this.#record({ replace: this.#replacing });
  };

  /** The case size and frequency the pace question's answer carries, if there is one. */
  #pace() {
    const input = this.querySelector('[data-axis="pace"] input:checked');
    if (!(input instanceof HTMLInputElement)) return {};
    const size = Number(input.dataset.size);
    const cadence = Number(input.dataset.cadence);
    return {
      size: Number.isFinite(size) && size > 0 ? size : undefined,
      cadence: Number.isFinite(cadence) && cadence > 0 ? cadence : undefined,
    };
  }

  /**
   * Every answer the price question offers, in its order ("10-15", "15-20", "20-"), "Mix it up"
   * left out. The swap drawer orders its wines by these bands, nearest the shopper's first.
   * @returns {string[]}
   */
  #bands() {
    return [...this.querySelectorAll('[data-axis="price"] input')]
      .map((input) => (input instanceof HTMLInputElement ? input.value : ''))
      .filter(Boolean);
  }

  /**
   * @param {{ resumed?: boolean, view?: 'reveal' | 'case' }} [options] resumed when a finished
   *   quiz is being picked up again, or shown again by Back or Forward, which is not a second
   *   completion; view is the screen the result opens on, the reveal unless the case is asked for
   */
  #complete({ resumed = false, view = 'reveal' } = {}) {
    window.clearTimeout(this.#timer);
    this.#guard.clear();
    this.#editing = false;

    const profile = Object.fromEntries(this.#answers);
    // A customer who arrived from "Build a case with this wine" (?seed=<variant id>, set by the
    // card's club tab, the club card and the In the club line) gets that wine first in the case.
    // assets/club-scoring.js `seeded` ignores a seed that is not in the club pool or is sold out.
    const seed = new URLSearchParams(window.location.search).get('seed');
    if (seed && /^\d+$/.test(seed)) profile.seed = seed;
    // Ranked at the default size, as before: the result section builds a six and resizes from
    // there. `size` and `cadence` below are what the pace answer asks the case to OPEN on.
    const rankedAt = Number(this.dataset.caseSize) || DEFAULT_CASE_SIZE;
    const wines = recommend(this.#pool, profile, rankedAt);
    const seconds = this.#startedAt ? Math.round((Date.now() - this.#startedAt) / 1000) : 0;

    if (!resumed) {
      /** @type {Record<string, string>} */
      const flat = {};
      for (const question of QUIZ_QUESTIONS) {
        if (!this.#axes().includes(question)) continue;
        flat[question] = this.#answers.get(question) ?? '';
        flat[`${question}_label`] = this.#labelOf(question);
      }
      const { size, cadence } = this.#pace();
      track('club_quiz_completed', {
        profile: profileKey(profile), answers: profile, seconds, ...flat, case_size: size, cadence,
      });
    }

    const complete = () => this.dispatchEvent(
      new CustomEvent('wine-club:complete', {
        bubbles: true,
        // `axes` says which questions there are, so the reveal makes a pill a button only when
        // there is a question for it to open.
        detail: { profile, wines, poolSize: this.#pool.length, pool: this.#pool, view, bands: this.#bands(), axes: this.#axes(), ...this.#pace() },
      })
    );
    // On arrival the result section's script may not have run yet, and an event sent before it
    // listens is lost. Defining the element connects it, so the definition is what is waited for.
    if (customElements.get('club-result')) complete();
    else customElements.whenDefined('club-result').then(complete);
    // The result section takes over the page under the bar. Its screen is up as soon as Loop's
    // bundle has been read, and it says so with wine-club:view; the bar does not wait for that.
    this.#handOver(view);
    this.#record({ replace: this.#replacing });
  }

  #onRestart = () => {
    // Answers stay checked so a returning visitor is editing, not starting over; the timer
    // restarts so seconds measures this attempt, not the elapsed time since the first run.
    this.#editing = false;
    this.#startedAt = Date.now();
    this.#show(0);
  };

  /**
   * A reveal pill: open the question that set that answer, every other answer kept, and come back
   * to the result when the visitor moves on from it. Back from the question is the reveal again,
   * unchanged, because the reveal's history entry is the one under it.
   * @param {Event} event
   */
  #onEdit = (event) => {
    const index = questionFor(this.#axes(), /** @type {CustomEvent} */ (event).detail?.axis ?? '');
    if (index < 0) return;
    this.#editing = true;
    this.#startedAt = Date.now();
    this.#show(index);
  };

  /**
   * Start again, from the result: everything the welcome's Start again forgets (the answers, the
   * ticks and the saved case), then question 1. The result's own history entry names a screen that
   * no longer exists, so question 1 takes it over rather than stacking on top of it.
   */
  #onResultStartAgain = () => {
    this.#editing = false;
    this.#forget();
    this.#show(0, { record: 'replace' });
  };
}

if (!customElements.get('wine-club-quiz')) {
  customElements.define('wine-club-quiz', WineClubQuiz);
}
